import React, { ReactNode, useMemo, useContext } from 'react';
import {
  QueryClient,
  QueryClientProvider as TanstackQueryClientProvider,
  QueryFunctionContext,
  QueryCache,
  MutationCache,
} from '@tanstack/react-query';
import axiosInstance from '@/lib/enhancedAxiosInstance';
import { AuthContext, type AuthContextType } from '@/contexts/AuthContext';
import { logDebug, logError } from '@/utils/errorHandler';
import { toast } from 'sonner';

type QueryKeyStringArray = string[];

/**
 * Factory that returns a default QueryFunction which:
 * 1. Joins the queryKey array into a URL path
 * 2. Injects `organization_id` as a query param if present
 */
const createDefaultQueryFn =
  (organizationId?: string) =>
  async ({ queryKey }: QueryFunctionContext) => {
    // Build URL from key segments:
    // e.g. ['api', 'orgs', '123', 'memberships'] -> '/api/orgs/123/memberships'
    const url = '/' + (queryKey as string[]).join('/');

    // Append org if provided
    const finalUrl = organizationId
      ? url + (url.includes('?') ? '&' : '?') + `organization_id=${organizationId}`
      : url;

    logDebug('Query client fetching data', {
      url: finalUrl,
      organizationId: organizationId || 'none',
      queryKey: queryKey as string[],
    });
    const response = await axiosInstance.get(finalUrl);
    return response.data;
  };

/**
 * Global error handler for React Query
 * Prevents technical error messages from being shown to users
 */
const handleQueryError = (error: unknown) => {
  // Check if this is a 401 error (authentication error)
  const is401Error =
    error &&
    typeof error === 'object' &&
    'response' in error &&
    error.response &&
    typeof error.response === 'object' &&
    'status' in error.response &&
    error.response.status === 401;

  // Don't show any toasts for 401 errors - let the axios interceptor handle them
  if (is401Error) {
    return;
  }

  // Log the error for developers
  logError(
    error instanceof Error ? error : 'Query error',
    {
      component: 'QueryClient',
      action: 'globalErrorHandler',
    },
    {
      showToast: false, // Don't show technical errors to users
      captureInSentry: true,
      userFacing: false,
    }
  );

  // Show user-friendly error message
  const isNetworkError =
    error instanceof Error &&
    (error.message.includes('Network') || error.message.includes('fetch'));

  if (isNetworkError) {
    toast.error('Connection error. Please check your internet connection.');
  } else {
    // Don't show generic "API call failed" messages
    // Let specific components handle their own error messages
  }
};

interface QueryClientProviderProps {
  children: ReactNode;
}

/**
 * A wrapper over TanStack's QueryClientProvider that:
 * - Waits for auth to finish before enabling any queries
 * - Rebuilds the QueryClient whenever the org changes
 * - Automatically includes `organization_id` in every request
 */
export const QueryClientProvider: React.FC<QueryClientProviderProps> = ({ children }) => {
  // Use useContext directly to safely handle when AuthContext is not yet available
  const authContext = useContext(AuthContext) as AuthContextType | undefined;

  // If auth context is not available yet, create a disabled query client
  if (!authContext) {
    const initialClient = new QueryClient({
      queryCache: new QueryCache({
        onError: handleQueryError,
      }),
      mutationCache: new MutationCache({
        onError: handleQueryError,
      }),
      defaultOptions: {
        queries: {
          enabled: false,
          retry: false,
        },
      },
    });
    return (
      <TanstackQueryClientProvider client={initialClient}>{children}</TanstackQueryClientProvider>
    );
  }

  const { user, loading: authLoading } = authContext;
  const organizationId = user?.organization_id;

  // Recreate the QueryClient whenever authLoading or organizationId changes
  const queryClient = useMemo(() => {
    return new QueryClient({
      queryCache: new QueryCache({
        onError: handleQueryError,
      }),
      mutationCache: new MutationCache({
        onError: handleQueryError,
      }),
      defaultOptions: {
        queries: {
          // Only enable once auth is done and we have an org
          enabled: !authLoading && Boolean(organizationId),
          retry: 1,
          staleTime: 5 * 60 * 1000,
          queryFn: createDefaultQueryFn(organizationId),
        },
      },
    });
  }, [authLoading, organizationId]);

  // While auth is loading, disable every query to avoid empty-fetch races
  if (authLoading) {
    const loadingClient = new QueryClient({
      queryCache: new QueryCache({
        onError: handleQueryError,
      }),
      mutationCache: new MutationCache({
        onError: handleQueryError,
      }),
      defaultOptions: {
        queries: {
          enabled: false,
          retry: false,
        },
      },
    });
    return (
      <TanstackQueryClientProvider client={loadingClient}>{children}</TanstackQueryClientProvider>
    );
  }

  return <TanstackQueryClientProvider client={queryClient}>{children}</TanstackQueryClientProvider>;
};

export default QueryClientProvider;
